import { Entity, World } from "ecsy";
import { Player, PlayerStateChange } from "../components/player";
import Background from "../components/background";
import ControllerDesktop from "@/game/skifree/controller/ControllerDesktop";
import ControllerMobile from "@/game/skifree/controller/ControllerMobile";
import { PlayerDirection } from "@/game/skifree/controller/Controller";
import { toRadians, clamp } from "@/js/math/FMath";
import DisposableSystem from "@/game/libs/engine/root/disposable-system";
import { getLevel } from "@/game/skifree/difficulty";
import { getPack } from "@/game/libs/engine/loader/asset-loader";

export default class PlayerControlSystem extends DisposableSystem<{
  player: any;
  background: any;
}> {
  init() {
    this._desktopControl = new ControllerDesktop();
    this._desktopControl.onJump.connect(this.onJump.bind(this));
    this._desktopControl.onFace.changed.connect(
      this.onDirectionChange.bind(this)
    );
    this._mobileControl = new ControllerMobile();
    this._mobileControl.onJump.connect(this.onJump.bind(this));
    this._mobileControl.onFace.changed.connect(
      this.onDirectionChange.bind(this)
    );
  }

  execute(delta: number, time: number) {
    var bgEntity: Entity = this.queries.background.results[0];

    this.queries.player.results.forEach((entity: Entity) => {
      let player = entity.getComponent(Player);
      if (player.isCrashed) {
        player.jumpingElapsed = 0;
        this._playerJumpChange = null;
        return;
      }

      if (player.jumpingElapsed !== 0) {
        player.jumpingElapsed -= delta;
        if (player.jumpingElapsed <= 0) {
          (getPack().sound.land as Howl).play();
          player.jumpingElapsed = 0;
          var direction: PlayerDirection = player.direction;
          switch (direction) {
            case PlayerDirection.FACE_LEFT:
              player.sprite.loop("EOTP_skier_face_left");
              break;
            case PlayerDirection.FACE_SLIGHT_LEFT:
              player.sprite.loop("EOTP_skier_face_slight_left");
              break;
            case PlayerDirection.FACE_DOWN:
              player.sprite.loop("EOTP_skier_face_down");
              break;
            case PlayerDirection.FACE_SLIGHT_RIGHT:
              player.sprite.loop("EOTP_skier_face_slight_right");
              break;
            case PlayerDirection.FACE_RIGHT:
              player.sprite.loop("EOTP_skier_face_right");
              break;
          }
        }
      }

      if (this._playerStateChange != null) {
        this._playerStateChange(player);
      }
      if (this._playerJumpChange != null) {
        this._playerJumpChange(player);
      }

      if (player.angle == 0) {
        player.veloX = 0;
        player.veloY = 0;
      } else {
        var rads = toRadians(player.angle);
        player.veloX = Math.cos(rads) * this.getSpeed(player.y);
        player.veloY = Math.sin(rads) * this.getSpeed(player.y);
      }

      player.sprite.x = player.x += player.veloX * delta;
      player.sprite.y = player.y += player.veloY * delta;
      player.shadow.x = player.x + 7.5;
      player.shadow.y = player.y + 32;

      if (bgEntity) {
        var background = bgEntity.getComponent(Background);
        background.sprite.tilePosition.x = -player.sprite.x;
        background.sprite.tilePosition.y = -player.sprite.y;
      }
    });
  }

  onJump(): void {
    this._playerJumpChange = function(player) {
      switch (player.direction) {
        case PlayerDirection.FACE_LEFT:
        case PlayerDirection.FACE_SLIGHT_LEFT:
        case PlayerDirection.FACE_DOWN:
        case PlayerDirection.FACE_SLIGHT_RIGHT:
        case PlayerDirection.FACE_RIGHT:
          if (player.jumpingElapsed === 0) {
            (getPack().sound.jump as Howl).play();
            player.jumpingElapsed = PlayerControlSystem.JUMP_DURATION;
            var direction: PlayerDirection = player.direction;
            switch (direction) {
              case PlayerDirection.FACE_LEFT:
                player.sprite.loop("EOTP_skier_jump_face_left");
                break;
              case PlayerDirection.FACE_SLIGHT_LEFT:
                player.sprite.loop("EOTP_skier_jump_face_slight_left");
                break;
              case PlayerDirection.FACE_DOWN:
                player.sprite.loop("EOTP_skier_jump_face_down");
                break;
              case PlayerDirection.FACE_SLIGHT_RIGHT:
                player.sprite.loop("EOTP_skier_jump_face_slight_right");
                break;
              case PlayerDirection.FACE_RIGHT:
                player.sprite.loop("EOTP_skier_jump_face_right");
                break;
            }
          }
          break;
      }
      this._playerJumpChange = null;
    };
  }

  onDirectionChange(direction: PlayerDirection): void {
    this._playerStateChange = (player: Player) => {
      if (player.jumpingElapsed > 0) {
        this._playerStateChange = null;
        this._desktopControl.onFace._ = player.direction;
        return;
      }
      var lastDirection = player.direction;
      player.direction = direction;
      var angle = 0;
      switch (direction) {
        case PlayerDirection.FACE_UP:
          angle = 0;
          (getPack().sound.stop as Howl).play();
          switch (lastDirection) {
            case PlayerDirection.FACE_LEFT:
              player.sprite.loop("EOTP_skier_face_up_left_idle");
              break;
            case PlayerDirection.FACE_SLIGHT_LEFT:
              player.sprite.loop("EOTP_skier_face_up_left_idle");
              break;
            case PlayerDirection.FACE_DOWN:
              player.sprite.loop("EOTP_skier_face_up_left_idle");
              break;
            case PlayerDirection.FACE_SLIGHT_RIGHT:
              player.sprite.loop("EOTP_skier_face_up_right_idle");
              break;
            case PlayerDirection.FACE_RIGHT:
              player.sprite.loop("EOTP_skier_face_up_right_idle");
              break;
          }
          break;
        case PlayerDirection.FACE_LEFT:
          player.sprite.loop("EOTP_skier_face_left");
          angle = 240;
          break;
        case PlayerDirection.FACE_SLIGHT_LEFT:
          player.sprite.loop("EOTP_skier_face_slight_left");
          angle = 210;
          break;
        case PlayerDirection.FACE_DOWN:
          player.sprite.loop("EOTP_skier_face_down");
          angle = 180;
          break;
        case PlayerDirection.FACE_SLIGHT_RIGHT:
          player.sprite.loop("EOTP_skier_face_slight_right");
          angle = 150;
          break;
        case PlayerDirection.FACE_RIGHT:
          player.sprite.loop("EOTP_skier_face_right");
          angle = 120;
          break;
      }
      player.angle = angle;

      if (player.angle == 0) {
        player.veloX = 0;
        player.veloY = 0;
        player.angle = 0;
      } else {
        player.angle = angle - 90;
        var rads = toRadians(player.angle);
        player.veloX = Math.cos(rads) * this.getSpeed(player.y);
        player.veloY = Math.sin(rads) * this.getSpeed(player.y);
      }

      this._playerStateChange = null;
    };
  }

  public getSpeed(distance: number): number {
    return PlayerControlSystem.SPEED + 2 * getLevel(distance);
  }

  public dispose() {
    this._playerStateChange = null;
    this._playerJumpChange = null;
    this._desktopControl.dispose();
    this._mobileControl.dispose();
  }

  private static SPEED = 300;
  private static JUMP_DURATION = 0.9;
  private _desktopControl: ControllerDesktop = null as any;
  private _mobileControl: ControllerMobile = null as any;
  private _playerStateChange: PlayerStateChange = null; //keep systems and events in sync
  private _playerJumpChange: PlayerStateChange = null; //keep systems and events in sync
}

(PlayerControlSystem as any).queries = {
  player: {
    components: [Player]
  },
  background: {
    components: [Background]
  }
};
